import { Permission } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import type { ReactNode } from 'react';
import type { IconType } from 'react-icons';

export interface ConfigurationLink {
    to: string;
    label: ReactNode;
    permissions?: Permission[];
}

export interface ConfigurationSection {
    icon: IconType;
    label: ReactNode;
    buttons: ConfigurationLink[];
    permissions?: Permission[];
}

export const configurationSections: ConfigurationSection[] = [
    {
        icon: ICONS.NAVIGATION.USER_MANAGER,
        label: CommonMessage.NAVIGATION.USER_MANAGER,
        buttons: [
            {
                to: '/users',
                label: CommonMessage.OBJECTS.USER.PLURAL,
                permissions: [
                    { item: EntityType.USER, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.USER, source: EntityType.USER, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/user-groups',
                label: CommonMessage.OBJECTS.GROUP.PLURAL,
                permissions: [
                    { item: EntityType.USER_GROUP, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.USER_GROUP, source: EntityType.USER_GROUP, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/invites',
                label: CommonMessage.OBJECTS.INVITE.PLURAL,
                permissions: [{ item: EntityType.INVITE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/roles',
                label: CommonMessage.OBJECTS.ROLE.PLURAL,
                permissions: [{ item: EntityType.ROLE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/entity-shares',
                label: CommonMessage.OBJECTS.ENTITY_SHARE.PLURAL,
                permissions: [{ item: EntityType.ENTITY_SHARE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/user-badges',
                label: CommonMessage.OBJECTS.BADGE.PLURAL,
                permissions: [
                    { item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.IDENTIFICATION, source: EntityType.USER, right: Rights.READ, uid: '?' }
                ]
            }
        ]
    },
    {
        icon: ICONS.ENTITY.SPOT,
        label: CommonMessage.OBJECTS.SPOT.DISTRI_SPOTS,
        permissions: [
            { item: EntityType.SPOT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.SPOT, source: EntityType.SPOT, right: Rights.READ, uid: '?' },
            { item: EntityType.MODULE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.MODULE, source: EntityType.SPOT, right: Rights.READ, uid: '?' },
            { item: EntityType.TEMPLATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.TEMPLATE, source: EntityType.SPOT, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/spots',
                label: CommonMessage.OBJECTS.SPOT.DISTRI_SPOTS,
                permissions: [
                    { item: EntityType.SPOT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.SPOT, source: EntityType.SPOT, right: Rights.READ, uid: '?' }
                ]
            },
            // {
            //     to: '/modules',
            //     label: CommonMessage.OBJECTS.MODULE.PLURAL,
            //     permissions: [
            //         { item: EntityType.MODULE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            //         { item: EntityType.MODULE, source: EntityType.SPOT, right: Rights.READ, uid: '?' }
            //     ]
            // },
            {
                to: '/module-templates',
                label: CommonMessage.OBJECTS.MODULE.TEMPLATE_PLURAL,
                permissions: [{ item: EntityType.TEMPLATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/slot-templates',
                label: CommonMessage.OBJECTS.SLOT.TEMPLATE_PLURAL,
                permissions: [{ item: EntityType.TEMPLATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.ORGANIZATION,
        label: CommonMessage.NAVIGATION.ORGANIZATION,
        permissions: [{ item: EntityType.ACCOUNT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }],
        buttons: [
            {
                to: '/accounts',
                label: CommonMessage.OBJECTS.ACCOUNT.PLURAL,
                permissions: [{ item: EntityType.ACCOUNT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/brandings',
                label: CommonMessage.OBJECTS.BRANDING.PLURAL,
                permissions: [{ item: EntityType.BRANDING, source: EntityType.ALL, right: Rights.SUPER_ADMIN, uid: '?' }]
            },
            {
                to: '/wizards',
                label: CommonMessage.OBJECTS.WIZARD.PLURAL,
                permissions: [{ item: EntityType.WIZARD, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/functionalities',
                label: CommonMessage.OBJECTS.FUNCTIONALITY.PLURAL,
                permissions: [{ item: EntityType.FUNCTIONALITY, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/identifications',
                label: CommonMessage.OBJECTS.IDENTIFICATION.PLURAL,
                permissions: [{ item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.INVENTORY,
        label: CommonMessage.NAVIGATION.INVENTORY,
        permissions: [
            { item: EntityType.STOCK, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.STOCK, source: EntityType.STOCK, right: Rights.READ, uid: '?' },
            { item: EntityType.STOCK_REQUIREMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.MOVEMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.MOVEMENT, source: EntityType.STOCK, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/stocks',
                label: CommonMessage.OBJECTS.STOCK.DEFAULT,
                permissions: [
                    { item: EntityType.STOCK, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.STOCK, source: EntityType.STOCK, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/stock-updates',
                label: CommonMessage.OBJECTS.STOCK.UPDATE.PLURAL,
                permissions: [
                    { item: EntityType.MOVEMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.MOVEMENT, source: EntityType.STOCK, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/requirements',
                label: CommonMessage.OBJECTS.REQUIREMENT.PLURAL,
                permissions: [{ item: EntityType.STOCK_REQUIREMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            },
            {
                to: '/baskets',
                label: CommonMessage.OBJECTS.BASKET.PLURAL,
                permissions: [
                    { item: EntityType.BASKET, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.BASKET, source: EntityType.BASKET, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/orders',
                label: CommonMessage.OBJECTS.ORDER.PLURAL,
                permissions: [
                    { item: EntityType.ORDER, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.ORDER, source: EntityType.ORDER, right: Rights.READ, uid: '?' }
                ]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.EQUIPMENT,
        label: CommonMessage.NAVIGATION.EQUIPMENT,
        permissions: [
            { item: EntityType.EQUIPMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.EQUIPMENT, source: EntityType.EQUIPMENT, right: Rights.READ, uid: '?' },
            { item: EntityType.EQUIPMENT_GROUP, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.EQUIPMENT_GROUP, source: EntityType.EQUIPMENT_GROUP, right: Rights.READ, uid: '?' },
            { item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.IDENTIFICATION, source: EntityType.EQUIPMENT, right: Rights.READ, uid: '?' },
            { item: EntityType.PROBLEM, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.PROBLEM, source: EntityType.EQUIPMENT, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/equipment-products',
                label: CommonMessage.OBJECTS.EQUIPMENT.PLURAL,
                permissions: [
                    { item: EntityType.EQUIPMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.EQUIPMENT, source: EntityType.EQUIPMENT, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/equipment-groups',
                label: CommonMessage.OBJECTS.GROUP.PLURAL,
                permissions: [
                    { item: EntityType.EQUIPMENT_GROUP, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.EQUIPMENT_GROUP, source: EntityType.EQUIPMENT_GROUP, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/tags',
                label: CommonMessage.OBJECTS.TAG.PLURAL,
                permissions: [
                    { item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.IDENTIFICATION, source: EntityType.EQUIPMENT, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/problems',
                label: CommonMessage.OBJECTS.PROBLEM.PLURAL,
                permissions: [
                    { item: EntityType.PROBLEM, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PROBLEM, source: EntityType.EQUIPMENT, right: Rights.READ, uid: '?' }
                ]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.CONTENT_AND_MEDIA,
        label: CommonMessage.NAVIGATION.CONTENT_AND_MEDIA,
        permissions: [
            { item: EntityType.PLAYLIST, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.PLAYLIST, source: EntityType.PLAYLIST, right: Rights.READ, uid: '?' },
            { item: EntityType.QUESTION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.QUESTION, source: EntityType.QUESTION, right: Rights.READ, uid: '?' },
            { item: EntityType.QUESTIONNAIRE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.QUESTIONNAIRE, source: EntityType.QUESTIONNAIRE, right: Rights.READ, uid: '?' },
            { item: EntityType.DOCUMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.DOCUMENT, source: EntityType.DOCUMENT, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/playlists',
                label: CommonMessage.NAVIGATION.PLAYLISTS,
                permissions: [
                    { item: EntityType.PLAYLIST, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PLAYLIST, source: EntityType.PLAYLIST, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/questions',
                label: CommonMessage.OBJECTS.QUESTION.PLURAL,
                permissions: [
                    { item: EntityType.QUESTION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.QUESTION, source: EntityType.QUESTION, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/questionnaires',
                label: CommonMessage.OBJECTS.QUESTION_GROUP.PLURAL,
                permissions: [
                    { item: EntityType.QUESTIONNAIRE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.QUESTIONNAIRE, source: EntityType.QUESTIONNAIRE, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/documents',
                label: CommonMessage.OBJECTS.DOCUMENT.MEDIA_FILES,
                permissions: [
                    { item: EntityType.DOCUMENT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.DOCUMENT, source: EntityType.DOCUMENT, right: Rights.READ, uid: '?' }
                ]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.QUOTAS_AND_CONSTRAINTS,
        label: CommonMessage.NAVIGATION.QUOTAS_AND_CONSTRAINTS,
        permissions: [
            { item: EntityType.BUDGET, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.BUDGET, source: EntityType.BUDGET, right: Rights.READ, uid: '?' },
            { item: EntityType.PRODUCT_GROUP, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.PRODUCT_GROUP, source: EntityType.PRODUCT_GROUP, right: Rights.READ, uid: '?' },
            { item: EntityType.COST_CENTER, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.COST_CENTER, source: EntityType.COST_CENTER, right: Rights.READ, uid: '?' },
            { item: EntityType.BUNDLE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.BUNDLE, source: EntityType.BUNDLE, right: Rights.READ, uid: '?' },
            { item: EntityType.CONSTRAINT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.CONSTRAINT, source: EntityType.SPOT, right: Rights.READ, uid: '?' },
            { item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.TRANSACTION, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/budgets',
                label: CommonMessage.OBJECTS.BUDGET.PLURAL,
                permissions: [
                    { item: EntityType.BUDGET, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.BUDGET, source: EntityType.BUDGET, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/transaction-rules',
                label: CommonMessage.OBJECTS.TRANSACTION_RULE.PLURAL,
                permissions: [
                    { item: EntityType.TRANSACTION_RULE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.TRANSACTION_RULE, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/product-groups',
                label: CommonMessage.OBJECTS.PRODUCT_GROUP.PLURAL,
                permissions: [
                    { item: EntityType.PRODUCT_GROUP, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PRODUCT_GROUP, source: EntityType.PRODUCT_GROUP, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/cost-centers',
                label: CommonMessage.OBJECTS.COST_CENTER.PLURAL,
                permissions: [
                    { item: EntityType.COST_CENTER, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.COST_CENTER, source: EntityType.COST_CENTER, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/bundles',
                label: CommonMessage.OBJECTS.BUNDLE.PLURAL,
                permissions: [
                    { item: EntityType.BUNDLE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.BUNDLE, source: EntityType.BUNDLE, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/constraints',
                label: CommonMessage.OBJECTS.CONSTRAINT.PLURAL,
                permissions: [{ item: EntityType.CONSTRAINT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.IOT,
        label: CommonMessage.OBJECTS.IOT.DEFAULT,
        permissions: [
            { item: EntityType.IOT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.IOT, source: EntityType.IOT, right: Rights.READ, uid: '?' },
            { item: EntityType.DEVICE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.DEVICE, source: EntityType.IOT, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/iots',
                label: CommonMessage.OBJECTS.IOT.DEFAULT,
                permissions: [
                    { item: EntityType.IOT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.IOT, source: EntityType.IOT, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/unknowns',
                label: CommonMessage.OBJECTS.UNKNOWN.DEFAULT
            }
            // {
            //     to: '/devices',
            //     label: CommonMessage.OBJECTS.DEVICE.PLURAL,
            //     permissions: [
            //         { item: EntityType.DEVICE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            //         { item: EntityType.DEVICE, source: EntityType.IOT, right: Rights.READ, uid: '?' }
            //     ]
            // }
        ]
    },
    {
        icon: ICONS.ENTITY.WORKFLOW,
        label: CommonMessage.OBJECTS.WORKFLOW.PLURAL,
        permissions: [
            { item: EntityType.WORKFLOW, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.WORKFLOW, source: EntityType.WORKFLOW, right: Rights.READ, uid: '?' },
            { item: EntityType.USER_INTERFACE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.USER_INTERFACE, source: EntityType.USER_INTERFACE, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/workflows',
                label: CommonMessage.OBJECTS.WORKFLOW.PLURAL,
                permissions: [
                    { item: EntityType.WORKFLOW, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.WORKFLOW, source: EntityType.WORKFLOW, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/user-interfaces',
                label: CommonMessage.OBJECTS.USER_INTERFACE.PLURAL,
                permissions: [
                    { item: EntityType.USER_INTERFACE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.USER_INTERFACE, source: EntityType.USER_INTERFACE, right: Rights.READ, uid: '?' }
                ]
            }
        ]
    },
    {
        icon: ICONS.ENTITY.TRANSACTION,
        label: CommonMessage.NAVIGATION.TRANSACTIONS,
        permissions: [
            { item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.TRANSACTION, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' },
            { item: EntityType.PACKAGE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.PACKAGE, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' },
            { item: EntityType.COLLI, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.COLLI, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/transactions',
                label: CommonMessage.OBJECTS.TRANSACTION.PLURAL,
                permissions: [
                    { item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.TRANSACTION, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/packages',
                label: CommonMessage.OBJECTS.PACKAGE.PLURAL,
                permissions: [
                    { item: EntityType.PACKAGE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PACKAGE, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/collis',
                label: CommonMessage.OBJECTS.COLLI.PLURAL,
                permissions: [
                    { item: EntityType.COLLI, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.COLLI, source: EntityType.TRANSACTION, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/pin-codes',
                label: CommonMessage.OBJECTS.PIN_CODE.PLURAL,
                permissions: [{ item: EntityType.PIN_CODE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.PRODUCT,
        label: CommonMessage.NAVIGATION.PRODUCT,
        permissions: [
            { item: EntityType.PRODUCT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.PRODUCT, source: EntityType.PRODUCT, right: Rights.READ, uid: '?' },
            { item: EntityType.ATTRIBUTE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.ATTRIBUTE, source: EntityType.PRODUCT, right: Rights.READ, uid: '?' },
            { item: EntityType.CERTIFICATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.CERTIFICATE, source: EntityType.PRODUCT, right: Rights.READ, uid: '?' },
            { item: EntityType.CERTIFICATE, source: EntityType.USER, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/products',
                label: CommonMessage.OBJECTS.PRODUCT.PLURAL,
                permissions: [
                    { item: EntityType.PRODUCT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PRODUCT, source: EntityType.PRODUCT, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/attributes',
                label: CommonMessage.OBJECTS.ATTRIBUTE.PLURAL,
                permissions: [
                    { item: EntityType.ATTRIBUTE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.ATTRIBUTE, source: EntityType.PRODUCT, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/product-categories',
                label: (
                    <>
                        {CommonMessage.OBJECTS.CATEGORY.PLURAL} / {CommonMessage.OBJECTS.CATALOG.PLURAL}
                    </>
                ),
                permissions: [
                    { item: EntityType.PRODUCT, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PRODUCT, source: EntityType.PRODUCT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PRODUCT_CATEGORY, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.PRODUCT_CATEGORY, source: EntityType.PRODUCT_CATEGORY, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/certificates',
                label: CommonMessage.OBJECTS.CERTIFICATE.PLURAL,
                permissions: [
                    { item: EntityType.CERTIFICATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.CERTIFICATE, source: EntityType.PRODUCT, right: Rights.READ, uid: '?' },
                    { item: EntityType.CERTIFICATE, source: EntityType.USER, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/reviews',
                label: CommonMessage.OBJECTS.REVIEW.PLURAL
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.NOTIFICATION_TEMPLATE,
        label: CommonMessage.OBJECTS.NOTIFICATION.PLURAL,
        permissions: [
            { item: EntityType.NOTIFICATION_TEMPLATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.NOTIFICATION_TEMPLATE, source: EntityType.NOTIFICATION_TEMPLATE, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/notifications',
                label: CommonMessage.OBJECTS.NOTIFICATION.PLURAL,
                permissions: [
                    { item: EntityType.NOTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.NOTIFICATION, source: EntityType.NOTIFICATION_TEMPLATE, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/notification-templates',
                label: CommonMessage.OBJECTS.NOTIFICATION_TEMPLATE.PLURAL,
                permissions: [
                    { item: EntityType.NOTIFICATION_TEMPLATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.NOTIFICATION_TEMPLATE, source: EntityType.NOTIFICATION_TEMPLATE, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/labels',
                label: CommonMessage.OBJECTS.LABEL.PLURAL,
                permissions: [
                    { item: EntityType.LABEL, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.LABEL, source: EntityType.LABEL, right: Rights.READ, uid: '?' }
                ]
            },
            {
                to: '/label-templates',
                label: CommonMessage.OBJECTS.LABEL_TEMPLATE.PLURAL,
                permissions: [{ item: EntityType.LABEL_TEMPLATE, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.IMPORT,
        label: CommonMessage.OBJECTS.IMPORT.DEFAULT,
        permissions: [{ item: EntityType.IMPEX, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }],
        buttons: [
            {
                to: '/imports',
                label: CommonMessage.OBJECTS.IMPORT.PLURAL,
                permissions: [{ item: EntityType.IMPEX, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' }]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.REPORT,
        label: CommonMessage.LICENSES.ANALYTICS.DEFAULT,
        buttons: [
            {
                to: '/reports',
                label: CommonMessage.OBJECTS.REPORT.PLURAL
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.DEBUG,
        label: CommonMessage.DETAIL.DEBUG,
        permissions: [{ item: EntityType.ACCOUNT, source: EntityType.ACCOUNT, right: Rights.SUPER_ADMIN, uid: '?' }],
        buttons: [
            {
                to: '/docker-images',
                label: CommonMessage.OBJECTS.DOCKER_IMAGE.PLURAL,
                permissions: [{ item: EntityType.DOCKER_IMAGE, source: EntityType.ACCOUNT, right: Rights.SUPER_ADMIN, uid: '*' }]
            }
        ]
    },
    {
        icon: ICONS.NAVIGATION.NIVALIZ,
        label: CommonMessage.NAVIGATION.NIVALIZ,
        permissions: [
            { item: EntityType.DECEASED, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
            { item: EntityType.DECEASED, source: EntityType.DECEASED, right: Rights.READ, uid: '?' }
        ],
        buttons: [
            {
                to: '/deceased',
                label: CommonMessage.OBJECTS.DECEASED.PLURAL,
                permissions: [
                    { item: EntityType.DECEASED, source: EntityType.ACCOUNT, right: Rights.READ, uid: '?' },
                    { item: EntityType.DECEASED, source: EntityType.DECEASED, right: Rights.READ, uid: '?' }
                ]
            }
        ]
    }
];
